import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Container, Grid, TextField, MenuItem, TableRow, TableCell, IconButton, Tooltip, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/EditOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { toast } from 'react-toastify';
import moment from "moment";
import api from '../../../services/api.js';
import { pode, nomePerfil, mensagemErro } from '../../../services/acesso';
import PageHeader from '../../../components/PageHeader';
import FilterBar from '../../../components/FilterBar';
import ConfirmDialog from '../../../components/ConfirmDialog';
import TabelaPaginada, { lerPaginacao } from '../../../components/TabelaPaginada';

const STATUS = [
  { valor: "", nome: "Todos" },
  { valor: "true", nome: "Ativo" },
  { valor: "false", nome: "Inativo" },
];
const FILTROS_INICIAIS = { name: "", status: "true" };

/**
 * Usuários.
 * Correção importante: a paginação era fixa em 1 página (count={1}), então usuários além da
 * primeira página do endpoint "assessores" nunca apareciam. Agora a página é enviada e o total
 * vem da resposta (se o endpoint não for paginado, mostra tudo e esconde a paginação).
 */
export default function ListingUser() {
  const history = useHistory();
  const gerenciar = pode("gerenciar_usuarios");
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [aplicados, setAplicados] = useState(FILTROS_INICIAIS);
  const [page, setPage] = useState(1);
  const [dados, setDados] = useState({ linhas: [], total: 0, totalPaginas: 0, porPagina: null });
  const [loading, setLoading] = useState(true);
  const [excluir, setExcluir] = useState(null);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    let ativo = true;
    setLoading(true);
    api.get("assessores", {
      params: {
        page,
        name: aplicados.name || undefined,
        status: aplicados.status === "" ? undefined : aplicados.status === "true",
      }
    })
      .then((result) => { if (ativo) setDados(lerPaginacao(result, null)); })
      .catch((e) => { if (ativo) toast.error(mensagemErro(e, "Não foi possível carregar os usuários")); })
      .finally(() => { if (ativo) setLoading(false); });
    return () => { ativo = false; };
  }, [page, aplicados, versao]);

  const aplicar = (novos) => {
    setAplicados(novos);
    setPage(1);
    setVersao((v) => v + 1);
  };

  const limpar = () => {
    const vazios = { name: "", status: "" };
    setFiltros(vazios);
    aplicar(vazios);
  };

  const remover = (campo) => () => {
    const novos = { ...aplicados, [campo]: "" };
    setFiltros(novos);
    aplicar(novos);
  };

  const ativos = [
    aplicados.name && { rotulo: `Nome: ${aplicados.name}`, remover: remover("name") },
    aplicados.status !== "" && { rotulo: `Status: ${aplicados.status === "true" ? "Ativo" : "Inativo"}`, remover: remover("status") },
  ].filter(Boolean);

  const handleDeleteClick = () => {
    api.delete(`assessor/${excluir.id}`).then(() => {
      toast.success("Usuário excluído");
      setVersao((v) => v + 1);
    }).catch((e) => toast.error(mensagemErro(e, "Não foi possível excluir o usuário")));
  };

  return (
    <Container maxWidth="lg">
      <PageHeader
        titulo="Usuários"
        acoes={gerenciar && (
          <>
            <Button variant="outlined" component={Link} to="/users/convites">
              Convites
            </Button>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} component={Link} to="/user/new">
              Novo usuário
            </Button>
          </>
        )}
      />

      <FilterBar onPesquisar={() => aplicar(filtros)} onLimpar={limpar} ativos={ativos}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField id="filtro_nome" label="Nome" value={filtros.name} onChange={(e) => setFiltros({ ...filtros, name: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField id="filtro_status" select label="Status" value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })} fullWidth>
              {STATUS.map((s) => <MenuItem key={s.nome} value={s.valor}>{s.nome}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </FilterBar>

      <TabelaPaginada
        rotulo="Lista de usuários"
        colunas={[
          { titulo: "Nome" }, { titulo: "E-mail" }, { titulo: "Perfil" }, { titulo: "Status" },
          { titulo: "Criado em" }, ...(gerenciar ? [{ titulo: "Ações", align: "right" }] : []),
        ]}
        carregando={loading}
        vazio={dados.linhas.length === 0}
        textoVazio="Nenhum usuário encontrado"
        temFiltro={ativos.length > 0}
        onLimpar={limpar}
        pagina={page}
        totalPaginas={dados.totalPaginas}
        totalRegistros={dados.total}
        porPagina={dados.porPagina}
        onPagina={setPage}
      >
        {dados.linhas.map((row) => (
          <TableRow
            key={row.id}
            hover
            style={{ cursor: gerenciar ? "pointer" : "default" }}
            onClick={gerenciar ? () => history.push("/user/" + row.id + "/edit") : undefined}
          >
            <TableCell style={{ fontWeight: 600 }}>{row.name}</TableCell>
            <TableCell>{row.email}</TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>
              {nomePerfil(row.perfil)}
              {Array.isArray(row.acoes_extras) && row.acoes_extras.length > 0 && (
                <span style={{ marginLeft: 6, background: "#EAF3E5", color: "#1F4615", borderRadius: 10, padding: "2px 8px", fontSize: 12, fontWeight: 600 }}>
                  +{row.acoes_extras.length} extra
                </span>
              )}
            </TableCell>
            <TableCell>
              <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: row.active ? "#E4EFDF" : "#EEF1EC", color: row.active ? "#1F4615" : "#3D4A38" }}>
                {row.active ? "Ativo" : "Inativo"}
              </span>
            </TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{row.created_at ? moment(row.created_at, "YYYY-MM-DD HH:mm").format("DD/MM/YYYY") : ""}</TableCell>
            {gerenciar && (
              <TableCell align="right" style={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                <Tooltip title="Editar usuário">
                  <IconButton size="small" component={Link} to={"/user/" + row.id + "/edit"} aria-label={`Editar ${row.name}`}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Excluir usuário">
                  <IconButton size="small" aria-label={`Excluir ${row.name}`} onClick={() => setExcluir(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TabelaPaginada>

      <ConfirmDialog
        title="Excluir usuário?"
        open={Boolean(excluir)}
        setOpen={(v) => { if (!v) setExcluir(null); }}
        onConfirm={handleDeleteClick}
      >
        {excluir ? <><b>{excluir.name}</b> perderá o acesso ao CRM. Esta ação não pode ser desfeita.</> : null}
      </ConfirmDialog>
    </Container>
  );
}
